import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import type { MutateSurface } from "../message-builders";
import type { MutatorCtx } from "./messages-cores";

// ── B-2: shared schemas + cores for the invite create/accept Zero path ──
//
// The shared `invites.insert` mutator is a thin wrapper around the table-
// level `tx.mutate.invites.insert(...)` write. The server-mutator extension
// (in `server/server-mutators.ts`) wraps this client mutator with an
// override that adds the side effects the legacy `POST /invites/workspace`
// REST endpoint handles today:
//
//   • Admin+ role gate (workspace-scoped invites)
//   • Server-side token validation — clients mint tokens locally so the
//     share-link UI works without a round-trip. The server validates the
//     prefix + entropy and accepts the client token as-is when valid,
//     OR silently re-mints when malformed/predictable. See
//     `server/invite-side-effects.ts:isValidClientToken`.
//   • Sibling-invite revoke (revokes other pending invites for the same
//     (type, resource, invitee) tuple — single-use email targeting).
//   • Best-effort transactional invite email via `schedulePostCommit`
//     (fires only after the surrounding Zero/PG tx commits — discarded
//     on rollback).
//
// The client mutator is intentionally minimal: it inserts the row Zero-
// optimistically so the UI's reactive list updates immediately. On the
// rare server-side re-mint path (bad-RNG client), the authoritative row
// pushes back with the new token and the client UI reconciles via the
// push response. A bad-RNG client momentarily displays a stale share URL
// — acceptable since `mintClientInviteToken` uses WebCrypto.

export const inviteInsertSchema = z.object({
  id: z.string(),
  token: z.string(),
  type: z.enum(["workspace", "channel", "client_space", "dm", "group_dm"]),
  creator_id: z.string().optional(),
  invited_email: z.string().optional(),
  other_user_id: z.string().optional(),
  workspace_id: z.string().optional(),
  channel_id: z.string().optional(),
  client_space_id: z.string().optional(),
  max_uses: z.number().optional(),
  use_count: z.number(),
  expires_at: z.number(),
  revoked: z.boolean(),
  scope: z.string().optional(),
  created_at: z.number(),
});

export type InviteInsertArgs = z.infer<typeof inviteInsertSchema>;

/**
 * Shared invite-insert core. Used by both the client `invitesMutators.insert`
 * and the server-mutator extension. Keep it intentionally thin — the
 * server-mutator override adds the heavyweight side effects (token mint,
 * admin gate, sibling revoke, email). This core's only job is the domain
 * INSERT + the `creator_id` ownership clamp.
 */
export async function inviteInsertCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: InviteInsertArgs;
  ctx: MutatorCtx;
}): Promise<void> {
  let toInsert = args;
  if (tx.location === "server") {
    // creator_id is optional in the Zod schema but must match auth user
    // when present. Force creator_id to ctx.sub to prevent omission bypass.
    toInsert = { ...args, creator_id: ctx?.sub };
  }
  await tx.mutate.invites.insert({
    ...toInsert,
    // Normalize invited_email to lowercase. The `invited_user_by_email`
    // Zero relationship is a strict-equality join to lowercase
    // `users.email` (migration 147), so `invites.receivedByMe` only finds
    // email-only invites when invited_email is lowercase too. This is the
    // chokepoint for every mutator-path create; migration 148's trigger is
    // the storage-layer backstop for the non-mutator paths (REST/MCP). Both
    // layers keep the optimistic client row consistent with stored state.
    invited_email: toInsert.invited_email?.toLowerCase(),
    // Per-type single-use clamp. An EMAIL-targeted invite must be single-use,
    // matching the REST endpoints (invite-create-dm hardcodes 1; group_dm /
    // channel use `invited_email ? 1 : default`). The mutator path previously
    // wrote `args.max_uses` verbatim (optional → NULL = unlimited), letting a
    // client push an email-targeted invite the recipient could re-accept
    // indefinitely. Shareable links (no invited_email) keep their max_uses.
    max_uses: toInsert.invited_email ? 1 : toInsert.max_uses,
    scope: toInsert.scope ?? "individual",
  });
}

export const invitesMutators = {
  insert: defineMutator(inviteInsertSchema, async (params) => {
    await inviteInsertCore(params);
  }),
  update: defineMutator(
    z.object({
      id: z.string(),
      revoked: z.boolean().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.invites.where("id", args.id).one());
        if (!entity) return;
        if (entity.creator_id !== ctx.sub) {
          throw new Error("Unauthorized: not invite creator");
        }
      }
      await tx.mutate.invites.update(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.invites.where("id", args.id).one());
        if (!entity) return;
        if (entity.creator_id !== ctx.sub) {
          throw new Error("Unauthorized: not invite creator");
        }
      }
      await tx.mutate.invites.delete(args);
    },
  ),
};

export const declinedInviteInsertSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  invite_id: z.string(),
  declined_at: z.number(),
});

export type DeclinedInviteInsertArgs = z.infer<
  typeof declinedInviteInsertSchema
>;

/**
 * Shared decline-insert core. `user_id === ctx.sub` is the auth boundary.
 *
 * Declines are idempotent: `declined_invites` has a UNIQUE (user_id,
 * invite_id) index, so a second decline of the same invite (cross-device, or
 * a stale inbox row in another tab) would throw a duplicate-key violation
 * server-side — surfacing a raw PG error in the decline toast. Server-side,
 * no-op when a decline row already exists so a re-decline quietly lands the
 * user in the desired end state. The optimistic client insert still fires
 * (the row drops from `receivedByMe`'s declines filter immediately); the
 * authoritative server write reconciles to the existing row.
 */
export async function declinedInviteInsertCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: DeclinedInviteInsertArgs;
  ctx: MutatorCtx;
}): Promise<void> {
  if (ctx?.sub && args.user_id !== ctx.sub) {
    throw new Error("Unauthorized: user_id mismatch");
  }
  if (tx.location === "server") {
    const existing = await tx.run(
      zql.declined_invites
        .where("user_id", args.user_id)
        .where("invite_id", args.invite_id)
        .one(),
    );
    if (existing) return;
  }
  await tx.mutate.declined_invites.insert(args);
}

export const declinedInvitesMutators = {
  insert: defineMutator(declinedInviteInsertSchema, async (params) => {
    await declinedInviteInsertCore(params);
  }),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.declined_invites.where("id", args.id).one(),
        );
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.declined_invites.delete(args);
    },
  ),
};
